import { useEffect, useRef } from "react";
import { usePostHog } from "posthog-js/react";
import { useHoldDigitizingBanner } from "../digitizingServiceStorage";
import { StudioLink } from "./StudioLink";

interface EditorPromoCardProps {
  // The page whose download triggered it, for the events.
  source: string;
  onDone: () => void;
  // Called on "Open Stitch Studio", before onDone: the caller stops showing
  // the card for good, since they've now found Studio.
  onClicked?: () => void;
}

/**
 * Offers someone who just downloaded a design to keep working on it in
 * Stitch Studio, by opening the file they just downloaded there (Studio is
 * another origin, so it can't be handed the design itself). Laid out exactly
 * like ProUpsellCard -- centred, with a backdrop -- and sequenced the same
 * way by its caller: never mounted alongside the design-email modal or the
 * Pro card, which want the same screen space.
 */
export function EditorPromoCard({ source, onDone, onClicked }: EditorPromoCardProps) {
  const posthog = usePostHog();
  // destination tells these apart from the old /editor card's, which sent
  // the same events.
  const eventBase = { source, destination: "studio" };

  const handleClose = () => {
    posthog?.capture("editor_promo_dismissed", eventBase);
    onDone();
  };

  // Same double-click guard as ProUpsellCard: the second click of a
  // double-clicked download button would otherwise land on the backdrop and
  // close the card the instant it opened.
  const openedAtRef = useRef(Date.now());
  const handleBackdropClick = () => {
    if (Date.now() - openedAtRef.current < 300) return;
    handleClose();
  };

  // The digitizing service banner waits while this is on screen.
  useHoldDigitizingBanner();

  // Guards against StrictMode's double-invoked effects, as ProUpsellCard does.
  const shownFired = useRef(false);
  useEffect(() => {
    if (shownFired.current) return;
    shownFired.current = true;
    posthog?.capture("editor_promo_shown", { ...eventBase, trigger: "post_download" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus starts on the primary action, and Escape dismisses, as clicking
  // the backdrop or "Not now" does.
  const primaryRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    primaryRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Studio opens in a new tab, so the card closes behind it: this page, and
  // the design on it, are still here when they come back.
  const handleTry = () => {
    posthog?.capture("editor_promo_clicked", eventBase);
    onClicked?.();
    onDone();
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 1001,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="editor-promo-title"
        aria-describedby="editor-promo-description"
        data-testid="editor-promo"
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "1.5rem",
          width: "24rem",
          maxWidth: "100%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h3 id="editor-promo-title" style={{ marginTop: 0, marginBottom: "0.5rem", fontSize: "1.1rem" }}>
          Take your design further in Stitch Studio
        </h3>
        <p id="editor-promo-description" style={{ color: "#555", fontSize: "0.9rem", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Our full embroidery editor: open the file you just downloaded, then add a logo, shapes, fills or more text,
          and export for your machine. Free for now.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem" }}>
          <StudioLink source="create_promo" newTab onClick={handleTry} linkRef={primaryRef} style={primaryButton}>
            Open Stitch Studio
          </StudioLink>
          <button onClick={handleClose} style={secondaryButton}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

const primaryButton: React.CSSProperties = {
  flex: "1 1 auto",
  padding: "0.6rem 1rem",
  fontSize: "0.95rem",
  fontWeight: 600,
  border: "none",
  borderRadius: "4px",
  background: "#333",
  color: "white",
  cursor: "pointer",
  textAlign: "center",
  textDecoration: "none",
};

// Understated next to the primary: no fill or border, just muted text.
const secondaryButton: React.CSSProperties = {
  width: "auto",
  margin: 0,
  padding: "0.6rem 0.8rem",
  fontSize: "0.9rem",
  border: "none",
  borderRadius: "4px",
  background: "transparent",
  color: "#666",
  cursor: "pointer",
};
